import { useState } from 'react';
import { Button, ErrorBanner, Spinner, Textarea } from '@/shared/ui';
import { characterLabel } from '@/features/references/utils/referenceNotes';
import {
  isLiveOp,
  type ReferenceAngle,
  type ReferenceCharacter,
  type ReferenceOp,
} from '@/features/references/services/referencesApi';

const ANGLE_LABELS: Record<ReferenceAngle, string> = {
  front: 'Front',
  back: 'Back',
  side: 'Side',
};
const ANGLE_ORDER: ReferenceAngle[] = ['front', 'back', 'side'];

const LIVE_COPY: Record<string, string> = {
  reserving: 'Reserving credits…',
  queued: 'Waiting to start…',
  running: 'Drawing new character images…',
  cancelling: 'Stopping…',
};

/* What a finished-but-unsuccessful operation means for the user. Every one of them
   leaves the previous complete version selected, and says so — that is the part worth
   telling them. */
const FAILED_COPY: Record<string, string> = {
  failed: "The last regeneration didn't finish. Your current version is unchanged.",
  needs_attention:
    "The last regeneration couldn't be completed automatically. Your current version is unchanged — try again.",
  rejected: "The last regeneration couldn't start. Your current version is unchanged.",
  discarded:
    'The last regeneration was discarded because the characters changed while it ran. Your current version is unchanged.',
};

export type ReferenceCharacterCardProps = {
  character: ReferenceCharacter;
  /** No regeneration may be started now (another operation, an approval, a request). */
  locked: boolean;
  /** This character's regeneration request is in flight. */
  submitting: boolean;
  /** Regenerations left for the whole video. */
  jobRemaining: number | null;
  /** The server's cap on the notes block. */
  maxNotesChars: number;
  error?: string;
  dismissedOps: ReadonlySet<string>;
  onDismissOp: (opId: string) => void;
  onRegenerate: (notes: string) => void;
};

export function ReferenceCharacterCard({
  character,
  locked,
  submitting,
  jobRemaining,
  maxNotesChars,
  error,
  dismissedOps,
  onDismissOp,
  onRegenerate,
}: ReferenceCharacterCardProps) {
  /* The notes are ONE replaceable block, not a chain: the box starts as the notes the
     selected version was drawn with and editing it replaces them. The parent keys this
     card by version, so a new version resets the box to its own notes. */
  const [notes, setNotes] = useState(character.notes ?? '');
  const trimmed = notes.trim();
  const live: ReferenceOp | null = isLiveOp(character.activeOp) ? character.activeOp : null;
  const last = character.lastOp;
  const failure =
    !live && last && FAILED_COPY[last.state] && !dismissedOps.has(last.id) ? last : null;

  /* `null` means no cap is configured: there is nothing to run out of, so nothing to
     show or to disable on. A number is a cap the server enforces. */
  const characterOut = character.regenerationsRemaining !== null && character.regenerationsRemaining <= 0;
  const jobOut = jobRemaining !== null && jobRemaining <= 0;
  const noneLeft = characterOut || jobOut;
  const caps = [character.regenerationsRemaining, jobRemaining].filter((n): n is number => n !== null);
  const left = caps.length ? Math.max(0, Math.min(...caps)) : null;
  const tooLong = notes.length > maxNotesChars;
  const disabled = locked || submitting || noneLeft || !trimmed || tooLong;
  const name = character.name ?? characterLabel(character.charKey);

  let reason: string | null = null;
  if (noneLeft) {
    reason =
      characterOut
        ? 'This character has no regenerations left.'
        : 'This video has no character regenerations left.';
  } else if (locked && !submitting && !live) {
    reason = 'Wait for the current change to finish.';
  }

  return (
    <article className="ref-card" aria-label={name} aria-busy={Boolean(live) || submitting}>
      <header className="ref-card__head">
        <h3 className="ref-card__name">{name}</h3>
        <span className="ref-card__version">Version {character.version}</span>
      </header>

      <ul className="ref-card__images">
        {ANGLE_ORDER.map((angle) => {
          const image = character.images.find((i) => i.angle === angle);
          return (
            <li key={angle} className="ref-card__image">
              {image?.url ? (
                <img src={image.url} alt={`${name}, ${ANGLE_LABELS[angle].toLowerCase()} view`} />
              ) : (
                <div className="ref-skeleton" role="img" aria-label={`${ANGLE_LABELS[angle]} view unavailable`} />
              )}
              <span className="ref-card__angle">{ANGLE_LABELS[angle]}</span>
            </li>
          );
        })}
      </ul>

      {character.effectiveDescription ? (
        <p className="ref-card__desc">{character.effectiveDescription}</p>
      ) : null}

      {live ? (
        <p className="ref-card__status" role="status">
          <Spinner size={14} />
          <span>{LIVE_COPY[live.state] ?? 'Working…'}</span>
        </p>
      ) : null}

      {failure ? <ErrorBanner message={FAILED_COPY[failure.state]} onDismiss={() => onDismissOp(failure.id)} /> : null}
      {error ? <ErrorBanner message={error} /> : null}

      <Textarea
        label="Replacement notes"
        hint="Describe what to change. These replace any earlier notes and take precedence over the original description."
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={Boolean(live) || submitting}
        error={tooLong ? `Keep notes to ${maxNotesChars} characters.` : undefined}
      />
      <p className="ref-card__count" aria-live="polite">
        <span className={tooLong ? 'is-over' : undefined}>
          {notes.length}/{maxNotesChars}
        </span>
      </p>

      <div className="ref-card__actions">
        <Button
          size="sm"
          onClick={() => onRegenerate(trimmed)}
          disabled={disabled}
          loading={submitting}
        >
          Regenerate with notes
        </Button>
        {left !== null ? (
          <span className="ref-card__left">
            {left} regeneration{left === 1 ? '' : 's'} left
          </span>
        ) : null}
      </div>
      {reason ? <p className="ref-card__reason">{reason}</p> : null}
    </article>
  );
}
